import { computed } from 'mobx';

import type { IPresenceConstraint, TConstraints } from '@feathr/rachis';

import {
  CreativeClass,
  type ICreativeAttributes,
  type IDimensionDefinition,
} from './creatives_base';
import { Creative, getAspectRatioNotation } from './creatives_base';

// =============================================================================
// Audit Status Types
// =============================================================================

export type TCreativeAuditStatus =
  | 'not audited'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'unauditable';

export interface ICreativeAuditStatus {
  auditor: string;
  status: TCreativeAuditStatus;
  reason: string;
}

// =============================================================================
// Display Creative Interface
// =============================================================================

export interface IDisplayCreative extends ICreativeAttributes {
  readonly _cls:
    | CreativeClass.DisplayBannersnack
    | CreativeClass.DisplayImage
    | CreativeClass.DisplayVideo
    | CreativeClass.DisplayAdTag
    | CreativeClass.FacebookImage
    | CreativeClass.FacebookVideo;
  width: number;
  height: number;
  url: string;
}

export interface IDisplayable {
  creatives: IDisplayCreative[];
}

/**
 * Creative classes for the Display channel.
 */
export const DISPLAY_CREATIVE_CLASSES: CreativeClass[] = [
  CreativeClass.DisplayAdTag,
  CreativeClass.DisplayBannersnack,
  CreativeClass.DisplayImage,
  CreativeClass.DisplayVideo,
];

/**
 * Creative classes for the Meta channel.
 */
export const META_CREATIVE_CLASSES: CreativeClass[] = [
  CreativeClass.FacebookImage,
  CreativeClass.FacebookVideo,
];

/**
 * All visual ad creative classes (display + meta).
 */
export const ADS_CREATIVE_CLASSES: CreativeClass[] = [
  ...DISPLAY_CREATIVE_CLASSES,
  ...META_CREATIVE_CLASSES,
];

// =============================================================================
// Display Creative Constraints
// =============================================================================

export const displayCreativeConstraints: TConstraints<IDisplayCreative> = {
  ad_groups: {
    presence: (_, attributes): IPresenceConstraint | undefined => {
      if (attributes.ad_groups === null) {
        return {
          message: 'You must select at least one audience.',
        };
      }
      return undefined;
    },
  },
  name: {
    presence: {
      allowEmpty: false,
    },
    format: {
      pattern: /^[^;<>^]*$/,
      message: 'cannot contain any of the following: <>;^',
    },
  },
  spec: {
    presence: (...args: any[]) => {
      const attributes = args[1];
      if (
        !attributes.width ||
        !attributes.height ||
        attributes.width < 1 ||
        attributes.height < 1
      ) {
        return {
          message: `^A set of creative dimensions must be selected`,
        };
      }
      return {
        message: `^${attributes.name} is missing a valid spec and must be re-uploaded`,
      };
    },
  },
  width: {
    numericality: true,
    presence: {
      allowEmpty: false,
    },
  },
  height: {
    numericality: true,
    presence: {
      allowEmpty: false,
    },
  },
  url: {
    presence: {
      allowEmpty: false,
    },
    url: true,
  },
  destination_url: {
    url: (...args: any[]) => {
      const attributes = args[1];
      const isEmpty = !attributes.destination_url;
      if (isEmpty) {
        return undefined;
      }
      return true;
    },
  },
};

// =============================================================================
// Display Creative Class
// =============================================================================

export class DisplayCreative<T extends IDisplayCreative = IDisplayCreative> extends Creative<T> {
  public readonly className:
    | 'AdTagCreative'
    | 'BannersnackCreative'
    | 'DisplayCreative'
    | 'FacebookDisplayCreative'
    | 'FacebookImageCreative'
    | 'FacebookVideoCreative'
    | 'ImageCreative'
    | 'VideoCreative' = 'DisplayCreative';

  public override get constraints(): TConstraints<T> {
    return displayCreativeConstraints as TConstraints<T>;
  }

  public closestValidDimension(dimensions: Record<string, IDimensionDefinition>): string {
    let d = Infinity;
    let spec = 'None (0px x 0px)';
    const width = this.get('width');
    const height = this.get('height');
    Object.values(dimensions).forEach((dim) => {
      const dp = Math.hypot(width - dim.width, height - dim.height);
      if (dp < d) {
        d = dp;
        spec = dim.name + ' (' + dim.width + 'px x ' + dim.height + 'px)';
      }
    });
    return spec;
  }

  @computed
  public get creativeType(): string {
    return this.get('mimetype')?.includes('video') ? 'Video' : 'Image';
  }

  @computed
  public get isVideo(): boolean {
    const cls = this.get('_cls');
    return cls === CreativeClass.DisplayVideo || cls === CreativeClass.FacebookVideo;
  }

  @computed
  public get dimensions(): string {
    return this.get('width') + 'x' + this.get('height');
  }

  @computed
  public get aspectRatio(): string {
    return getAspectRatioNotation(this.aspectRatioDecimal);
  }

  @computed
  public get aspectRatioDecimal(): number {
    return this.get('width') / this.get('height');
  }

  public getClosestValidAspectRatio(dimensions: Record<string, IDimensionDefinition>): string {
    const aspectRatio = this.aspectRatioDecimal;
    const dimensionValues = Object.values(dimensions);
    const closest = dimensionValues.reduce((prev, curr) => {
      return Math.abs(curr.width / curr.height - aspectRatio) <
        Math.abs(prev.width / prev.height - aspectRatio)
        ? curr
        : prev;
    }, dimensionValues[0]);

    return getAspectRatioNotation(closest.width / closest.height);
  }
}
